import React, {useState} from 'react';
import Modal from 'react-modal';
import { Link } from "react-router-dom";
import Has from '../Permission/Has';
import PartyInfo from './PartyInfo/PartyInfo';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useIsPageDesktop } from '../../App';
import { useCompany } from '../../component/CompanySelection/CompanyContext';
import { toDateString, getLocalDateFromUTCDate } from '../../util/DateUtility';
import { formatDollarAmount } from '../../util/EfupUtility';
import { resendInviteNotification } from '../../service/NotificationService';
import { stopPayment } from '../../service/PaymentService';
import { getCheckImage } from '../../service/DocumentService';
import { FF_PAY_OVERVIEW_STOP_PAYMENT, FF_PAY_OVERVIEW_RESEND_EMAIL } from '../../config/FeatureFlagTypes';
import './PaymentOverview.css';

const VALID_CLOSE_PAYMENT_STATUSES = [ 'CHK_SNT',
                                       'ACH_SNT',
                                       'VC_SNT',
                                       'PMT_RETRY',
                                       'PVDR_SNT',
                                       'ECHK_SNT',
                                       'NTFCN_SNT',
                                       'SCHDLD',
                                       'PND_APRVL',
                                       'EXPRD',
                                       'PVDR_R_SNT',
                                       'APPRVD']

export default function PaymentOverview(props) {

    const isDesktop = useIsPageDesktop();
    const { companyNumber } = useCompany();
    const disbursement = props.disbursement;
    const paymentInfo = props.paymentInfo;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCheckImageLoading, setIsCheckImageLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmationResults, setIsConfirmationResults] = useState(false);
    const [messageConfirmationResults, setMessageConfirmationResults] = useState("Not Available");
    const [isError, setIsError] = useState(false);
    const [isStopPayment, setIsStopPayment] = useState(false);
    const [isResendNotification, setIsResendNotification] = useState(false);
    const [showPaymentInfo, setShowPaymentInfo] = useState(false);

    if (disbursement === null) {
        return <div>Payment not found.</div>
    }

    //check for null dates
    const createdDate = disbursement.createdDate ? toDateString(disbursement.createdDate) : "N/A";
    const disbursementDate = disbursement.disbursementDate ? toDateString(getLocalDateFromUTCDate(disbursement.disbursementDate)) : "N/A";
    const isRecurring = disbursement.isRecurring !== null ? disbursement.isRecurring ? 'Yes' : 'No' : "N/A";

    //disable/enable 'Stop Payment' btn based on disbursement status
    const enableStopPayment = () => {
        if(disbursement.status === 'PVDR_R_SNT' && disbursement.paymentMethod === 'Direct Deposit'){
            return false;
        }
        return VALID_CLOSE_PAYMENT_STATUSES.includes(disbursement.status);
    } 

    //enable/disable Resend Notification btn
    const enableResendNotification = () => {
        if(disbursement.portalPayment){
            //not MP Future Enanchment
            if(disbursement.parties.length <= 1){
                if(['NTFCN_SNT','P2D_DECLNE'].includes(disbursement.status)){
                    var party = disbursement.parties[0];
                    if(party.expirationDate){
                        var nowPlus10 = new Date(new Date().getTime() + 10*60000);
                        var expirationDate = new Date(party.expirationDate);
                        //key expired is not in 10mins or less
                        if(expirationDate.getTime() > nowPlus10.getTime()){
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    const resetConfirmationModal = (isStop, isResend, isCheckImage) => {
        setIsStopPayment(isStop);
        setIsResendNotification(isResend);
        setIsCheckImageLoading(isCheckImage);
        setIsConfirmationResults(false);
        setIsLoading(isCheckImage ? true : false);
        setIsModalOpen(true);
    }

    const getCheckImageModal = () => {
        resetConfirmationModal(false, false, true);
        fetch(getCheckImage(companyNumber, disbursement.uuid, getCheckImageModalCallbackHandler));
    }

    function getCheckImageModalCallbackHandler(response) {
        if (response?.status === 200) {
            const data = response.data;
            if(data){
                if(data.encodedFrontDocument) {
                    downloadFile(data.encodedFrontDocument, data.documentFrontName);
                    if(data.encodedBackDocument) {
                        downloadFile(data.encodedBackDocument, data.documentBackName);
                    }
                    setIsModalOpen(false);
                } else {
                    setMessageConfirmationResults("We are unable to process your request. Try again later.");
                    setIsError(true);
                }
            }
        } else {
            setMessageConfirmationResults("We are unable to process your request. Try again later.");
            setIsError(true);
        }
        //Pausing to see spinner and to let functions complete
        setTimeout(function(){
            setIsConfirmationResults(true);
            setIsLoading(false);
        }, 300);
    }

    function downloadFile(encodedDocument, name){
        const byteChar = window.atob(encodedDocument.replace('/\s/g', ''));
        var byteNumber = byteChar.length;
        var buffer = new ArrayBuffer(byteNumber);
        var view = new Uint8Array(buffer);
        for(var j = 0; j < byteNumber; j++){
            view[j] = byteChar.charCodeAt(j);
        }
        const file = new Blob([view], {type: "application/pdf"});
        const anchor = document.createElement("a");
        anchor.href = URL.createObjectURL(file);
        anchor.download = name;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }

    //modal logic
    const handleConfirmationModal = () => {
        setIsLoading(true);
        var request = null;
        if(isStopPayment){
            stopPayment(disbursement.uuid, companyNumber, confirmationModalCallbackHandler);
        } else if(isResendNotification) {
            if(disbursement.parties.length <= 1){
                var party = disbursement.parties[0];
                request = {disbursementUuid: disbursement.uuid, portalPayment: disbursement.portalPayment, 
                status: disbursement.status, expirationDate: party.expirationDate};
                resendInviteNotification(request, companyNumber, confirmationModalCallbackHandler);
            } else {
                setMessageConfirmationResults("Your request was not successful. The payment is in a state where the notification can't be resent.")
                setIsError(true);
            }
        }
    }
        
    function confirmationModalCallbackHandler(response) {
        if (isStopPayment) {
            if (response?.status === 200 && Number(response?.data?.status) === 200) {
                //Successful
                setMessageConfirmationResults(response.data.message !== "The payment has been successfully terminated." ? response.data.message : "Payment was successfully stopped.");
            } else if (response?.status === 400 || (Number(response?.data?.status) >= 300 && Number(response?.data?.status) < 400)) {
                //Invalid Request can not send email
                setMessageConfirmationResults("Your request was not successful. The payment is in a state that can't be stopped.");
                setIsError(true);
            } else {
                //Not Available 404 //Other Failures 500 
                setMessageConfirmationResults("Your request was not successful. Please try again.");
                setIsError(true);
            }
        } else if (isResendNotification) {
            if (response?.status === 200) {
                //Successful
                setMessageConfirmationResults("You have successfully requested the invite notification to be resent.");
            } else if (response?.status === 400) {
                //Invalid Request can not send email
                setMessageConfirmationResults("Your request was not successful. The payment is in a state where the notification can't be resent.");
                setIsError(true);
            } else {
                //Not Available 404 //Other Failures 500 
                setMessageConfirmationResults("Your request was not successful. Please try again.");
                setIsError(true);
            }
        }
        //Pausing to see spinner and to let functions complete
        setTimeout(function(){
            setIsConfirmationResults(true);
            setIsLoading(false);
        }, 300);
    }

    function toggleDropDownPaymentInfo() {
        var value = !showPaymentInfo;
        setShowPaymentInfo(value);
    }

    return (
        <div className="efup-payment-side-panel-overview" data-testid="efup-payment-side-panel-overview" tabIndex={0}>
            <table className="table table-borderless" data-testid="efup-overview-tab-body">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="efup-side-panel-label">Payment Number:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-payment-number">{disbursement.claimNumber}</td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">Created Date:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-create-date">{createdDate}</td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">Payment Date:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-payment-date">{disbursementDate}</td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">Amount:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-amount">
                            {disbursement?.amount ? 
                                formatDollarAmount(disbursement.amount) 
                                : <></>
                            }
                        </td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">Status:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-status">{disbursement.status}</td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">PreferPay Portal Payment:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-portal-payment">{disbursement.portalPayment ? "Yes" : "No"}</td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">Payment Method:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-payment-method">{disbursement.paymentMethod}</td>
                    </tr>
                    {props.paymentType &&
                        <tr>
                            <td className="efup-side-panel-label">Type:</td>
                            <td className="efup-side-panel-data" data-testid="efup-overview-tab-payment-type">{props.paymentType}</td>
                        </tr>
                    }
                    <tr>
                        <td className="efup-side-panel-label">eCheck Number:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-echeck-number">{disbursement.eCheckNumber}</td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">Check Number:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-check-number">
                            {disbursement.checkNumber && disbursement.status === 'PMT_RCON' ? 
                                <Link to="" onClick={getCheckImageModal} data-testid="efup-overview-tab-check-number-link">{disbursement.checkNumber}</Link>
                                : disbursement.checkNumber
                            }
                        </td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">Recurring Payment:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-recurring">{isRecurring}</td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">Disbursement UUID:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-uuid">{disbursement.uuid}</td>
                    </tr>
                    <tr>
                        <td className="efup-side-panel-label">Group ID:</td>
                        <td className="efup-side-panel-data" data-testid="efup-overview-tab-confirm-id">{disbursement.confirmationId}</td>
                    </tr>
                </tbody>
            </table>

            { paymentInfo.paymentMethod == 'Check' &&
                <div>
                    <hr className="mini-table-hr"/>
                    <button className='payment-info-dropdown-button'  data-testid="efup-overview-tab-payment-info-dropdown-button"
                            tabIndex={0} onKeyDown={({key}) => key === "Enter" ? toggleDropDownPaymentInfo() : null} onClick={() => toggleDropDownPaymentInfo()}>
                        <div className="efup-sub-header" data-testid="efup-overview-payment-info-header">Payment Info</div>
                        <i className="cb-icon icon-expand_more" id="payment-info-dropdown-icon" alt="Close Button"/>
                    </button>            
                    {showPaymentInfo && paymentInfo &&
                        <table style={{ display: showPaymentInfo ? 'table' : 'none' }}>    
                            <tr>
                                <td className="efup-side-panel-label">Payment Method:</td>
                                <td className="efup-side-panel-data payment-info-data" data-testid="efup-overview-tab-payment-method">{paymentInfo.paymentMethod}</td>
                            </tr>
                            <tr>
                                <td className="efup-side-panel-label">Address Line 1:</td>
                                <td className="efup-side-panel-data payment-info-data" data-testid="efup-overview-tab-address-line-1">{paymentInfo.addressLine1}</td>
                            </tr>
                            <tr>
                                <td className="efup-side-panel-label">Address Line 2:</td>
                                <td className="efup-side-panel-data payment-info-data" data-testid="efup-overview-tab-address-line-2">{paymentInfo.addressLine2}</td>
                            </tr>
                            <tr>
                                <td className="efup-side-panel-label">Address Line 3:</td>
                                <td className="efup-side-panel-data payment-info-data" data-testid="efup-overview-tab-address-line-3">{paymentInfo.addressLine3}</td>
                            </tr>
                            <tr>
                                <td className="efup-side-panel-label">City:</td>
                                <td className="efup-side-panel-data payment-info-data" data-testid="efup-overview-tab-city">{paymentInfo.city}</td>
                            </tr>
                            <tr>
                                <td className="efup-side-panel-label">State:</td>
                                <td className="efup-side-panel-data payment-info-data" data-testid="efup-overview-tab-state">{paymentInfo.state}</td>
                            </tr>
                            <tr>
                                <td className="efup-side-panel-label">Zip Code:</td>
                                <td className="efup-side-panel-data payment-info-data" data-testid="efup-overview-tab-zip">{paymentInfo.zip}</td>
                            </tr>
                        </table> 
                    }
                    <hr className="mini-table-hr"/>
                </div>
            }

            <div className="efup-sub-header" data-testid="efup-overview-tab-party-section">Party Info</div>
            {disbursement?.parties && disbursement.parties.map((party,i) => 
            <PartyInfo key={`party-info-${i}`}
                party={party}
                index={i}
            />
            )}
            <div className="ocButtons">
                <Has permission="PAY_OVERVIEW_STOP_PAYMENT" featureFlag={FF_PAY_OVERVIEW_STOP_PAYMENT}>
                    <button type="button" className={enableStopPayment() ? "oc-blue-btn" : "oc-greyOutline-filled-btn"} data-testid="efup-overview-tab-stop-payment-button"
                        disabled={!enableStopPayment()} onClick={() => { resetConfirmationModal(true , false, false); }}>Stop Payment</button>
                </Has>
                <Has featureFlag={FF_PAY_OVERVIEW_RESEND_EMAIL}>
                    <button type="button" className={enableResendNotification() ? "oc-green-btn" : "oc-greyOutline-filled-btn"} data-testid="efup-overview-tab-resend-email-button"
                        disabled={!enableResendNotification()} onClick={() => { resetConfirmationModal(false, true, false); }}>Resend Email Notification</button>
                </Has>
            </div>
            <Modal 
                isOpen={isModalOpen}
                overlayClassName="confirmation-overlay"
                className="confirmation-modal"
                onRequestClose={() => setIsModalOpen(false)}
                shouldCloseOnOverlayClick= {isCheckImageLoading && isLoading ? false : true}
                tabIndex={0}>
                <div className="efup-modal" id="confirmationModal" data-testid="efup-overview-tab-confirmation-modal">
                    <div className="cb-modal-dialog" role="document">
                        <div className="cb-modal-content">
                            {isCheckImageLoading && isLoading ? 
                                <div></div> : 
                                <div className="cb-modal-header">
                                    <h2 className="cb-modal-title" data-testid="efup-overview-tab-confirmation-modal-title">{isCheckImageLoading ? "Download Failed" : (isStopPayment ? "Stop Payment" : "Resend Notification")}</h2>
                                    <i
                                        className="cb-close cb-icon icon-close"
                                        aria-hidden="true" 
                                        data-dismiss="cb-modal" 
                                        aria-label="Close" 
                                        onClick={() => setIsModalOpen(false)}
                                        tabIndex={0} onKeyDown={({key}) => key === "Enter" ? setIsModalOpen(false) : ""}
                                        data-testid="efup-overview-tab-confirmation-modal-close-button"
                                    />
                                </div>
                            }
                            {isLoading ?
                                <div className="cb-modal-body">
                                    <div className='confirmation-modal-loading-spinner'>
                                        <LoadingSpinner blue data-testid='efup-overview-tab-confirmation-modal-loading-spinner' />
                                        <p className='loading-spinner-text'>In progress...</p>
                                    </div>
                                </div>:
                                <div>
                                {isConfirmationResults ?
                                    <div>
                                        <div className="cb-modal-body">
                                            <p className='confirmation-modal-result-message' data-testid='efup-overview-tab-confirmation-modal-result-message'>
                                                { isError && <i className="cb-icon icon-warning"></i> }
                                                {messageConfirmationResults}
                                            </p>
                                        </div>
                                        <div className="cb-modal-footer">
                                            <div className={isDesktop ? "": "mobileButtons"}>
                                                <button
                                                    type="button"
                                                    className="cb-btn cb-btn-secondary confirmation-modal-ok-btn"
                                                    data-testid="efup-overview-tab-confirmation-modal-ok-button"
                                                    data-dismiss="cb-modal"
                                                    onClick={() => setIsModalOpen(false)}>
                                                    OK
                                                </button>
                                            </div>
                                        </div>
                                    </div>:
                                    <div>
                                        <div className="cb-modal-body" data-testid="efup-overview-tab-confirmation-modal-areYouSure">
                                            {disbursement?.parties && isStopPayment &&
                                                <p> {disbursement.parties[0].isVendor ? 'Are you sure you want to stop this payment? By clicking "Yes, stop payment" you authorize all associated bulk payments to be stopped.' :
                                                    "Are you sure you want to stop this payment?"}</p>
                                            }
                                            {isResendNotification &&
                                                <p>Are you sure you want to resend this invite notification?</p>
                                            }
                                        </div>
                                        <div className="cb-modal-footer">
                                            <div className={isDesktop ? "": "mobileButtons"}>
                                                <button
                                                    type="button"
                                                    className="cb-btn cb-btn-primary confirmation-modal-confirm-btn"
                                                    data-testid="efup-overview-tab-confirmation-modal-confirm-button"
                                                    data-dismiss="cb-modal"
                                                    onClick={() => handleConfirmationModal()}>
                                                    {isStopPayment ? "Yes, stop payment" : "Yes, resend notification"}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="cb-btn cb-btn-outline-secondary confirmation-modal-cancel-btn"
                                                    data-testid="efup-overview-tab-confirmation-modal-cancel-button"
                                                    data-dismiss="cb-modal"
                                                    onClick={() => setIsModalOpen(false)}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
